import { authService } from '@/services/auth.service';
import { apiClient } from './api.client';

/**
 * Helper service for managing client data and operations
 */
class ClientHelper {
  private clientData: { id: string; name: string } | null = null;

  /**
   * Get the current client ID from the authenticated user
   * For clients with restricted access, returns their assigned client_project_id
   * For admins/developers, returns null (they can see all clients)
   */
  async getCurrentClientId(): Promise<string | null> {
    const user = await authService.getProfile();
    
    // Client users (viewer role) have a client_project_id
    if (user.role === 'client' && user.client_project_id) {
      return user.client_project_id;
    }
    
    // Admin and developer users don't have a default client
    return null;
  }

  /**
   * Get available clients for the current user
   * Client users will only see their assigned client
   * Admin/developer users will see all clients
   */
  async getAvailableClients(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await apiClient.get('/clients');
      
      if (response.data.success && response.data.data) {
        const clients = response.data.data.clients || response.data.data;
        
        // If user only has access to one client, cache it
        if (Array.isArray(clients) && clients.length === 1) {
          this.clientData = clients[0];
        }
        
        return Array.isArray(clients) ? clients : [];
      }
      
      return [];
    } catch (error) {
      console.error('Failed to fetch available clients:', error);
      
      // If API fails, try to return cached client data if available
      const clientId = await this.getCurrentClientId();
      if (clientId && this.clientData) {
        return [this.clientData];
      }
      
      return [];
    }
  }

  /**
   * Check if the current user is a restricted client user
   * (i.e., can only see their own client data)
   */
  async isRestrictedUser(): Promise<boolean> {
    const user = await authService.getProfile();
    return user.role === 'client' && !!user.client_project_id;
  }

  /**
   * Get the client ID to use for API calls
   * For restricted users, returns their assigned client
   * For others, returns the provided clientId or requires selection
   */
  async getClientIdForApiCall(requestedClientId?: string): Promise<string> {
    const currentClientId = await this.getCurrentClientId();
    
    // If user is restricted to a specific client, always use that
    if (currentClientId) {
      return currentClientId;
    }
    
    // For admin/developer users, use the requested client ID
    if (requestedClientId) {
      return requestedClientId;
    }
    
    // If no client ID provided and user can see multiple clients, throw error
    throw new Error('Client ID is required for this operation');
  }

  /**
   * Validate if user has access to a specific client
   */
  async hasAccessToClient(clientId: string): Promise<boolean> {
    const currentClientId = await this.getCurrentClientId();
    
    // If user is restricted to a specific client, check if it matches
    if (currentClientId) {
      return currentClientId === clientId;
    }
    
    // Admin/developer users have access to all clients
    const user = await authService.getProfile();
    return user.role === 'admin' || user.role === 'developer';
  }

  /**
   * Clear cached client data (call on logout)
   */
  clearCache(): void {
    this.clientData = null;
  }
}

export const clientHelper = new ClientHelper();